import { useMutation, useQueryClient } from '@tanstack/react-query'
import { onSuccessInvalidate } from '@/hooks/invalidation'
import type { QueryDef } from '@/hooks/useDefinedQuery'
import { useDefinedQuery } from '@/hooks/useDefinedQuery'
import { withIdempotency } from '@/hooks/withIdempotency'
import {
  getSubscriptions,
  getSubscriptionsForItem,
  getSubscriptionStatus,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  bulkCreateFromBuylist,
} from '@/services/api/subscriptions'
import type { CreateSubscriptionData, UpdateSubscriptionData } from '@/services/api/subscriptions'
import type { SubscriptionKind, SubscriptionsResponse, SubscriptionStatusResponse } from '@/types'
import { isContentKind, isPriceKind } from '@/utils/subscriptionKinds'

const STALE_30S = 30_000

const subscriptionsQueries = {
  list: {
    queryKey: () => ['subscriptions'] as const,
    queryFn: getSubscriptions,
    staleTime: STALE_30S,
    auth: 'required',
  } satisfies QueryDef<Awaited<ReturnType<typeof getSubscriptions>>>,
  forItem: {
    queryKey: (type: 'Card' | 'SealedProduct', id: number) => ['subscriptions', 'forItem', type, id] as const,
    queryFn: (type: 'Card' | 'SealedProduct', id: number) => getSubscriptionsForItem(type, id),
    staleTime: STALE_30S,
    auth: 'required',
  } satisfies QueryDef<
    Awaited<ReturnType<typeof getSubscriptionsForItem>>,
    [type: 'Card' | 'SealedProduct', id: number]
  >,
  status: {
    queryKey: (type: string, id: number) => ['subscriptions', 'status', type, id] as const,
    queryFn: (type: string, id: number) => getSubscriptionStatus(type, id),
    staleTime: STALE_30S,
    auth: 'required',
  } satisfies QueryDef<Awaited<ReturnType<typeof getSubscriptionStatus>>, [type: string, id: number]>,
}

/**
 * All of the current user's subscriptions, plus the price-alert / content-follow
 * partition — the single source for that split so pages don't re-filter `kind`.
 */
export function useSubscriptions() {
  const query = useDefinedQuery(subscriptionsQueries.list)
  const subscriptions = query.data?.subscriptions ?? []
  return {
    ...query,
    subscriptions,
    priceAlerts: subscriptions.filter((s) => isPriceKind(s.kind)),
    contentFollows: subscriptions.filter((s) => isContentKind(s.kind)),
  }
}

export function useSubscriptionsForItem(type: 'Card' | 'SealedProduct' | null, id: number | null) {
  return useDefinedQuery(subscriptionsQueries.forItem, type ?? 'Card', id ?? 0, { enabled: !!type && !!id })
}

export function useSubscriptionStatus(subscribableType: string | undefined, subscribableId: number | undefined) {
  return useDefinedQuery(subscriptionsQueries.status, subscribableType ?? '', subscribableId ?? 0, {
    enabled: !!subscribableType && !!subscribableId,
  })
}

export function useCreateSubscription() {
  const queryClient = useQueryClient()
  type Vars = CreateSubscriptionData & { idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ idempotencyKey, ...data }: Vars) => createSubscription(data, idempotencyKey),
    onSuccess: async (resp) => {
      const sub = resp.subscription
      // Append the new subscription to the global list cache.
      queryClient.setQueryData<SubscriptionsResponse>(subscriptionsQueries.list.queryKey(), (old) => {
        if (!old) return { subscriptions: [sub] }
        return { subscriptions: [...old.subscriptions, sub] }
      })
      // The global list and the per-item caches (the modal's Active Alerts list,
      // FollowButton's follow status) are distinct query keys, so a fresh
      // subscription must also land in the right per-item cache by kind, or it
      // won't appear until that query refetches (#202).
      if (isContentKind(sub.kind)) {
        // FollowButton reads the content-follow status cache. Follow is a single
        // click, so the item's initial status GET can still be in flight and, if
        // it resolves after this seed, would overwrite it with the pre-follow
        // value (flipping the button back). Cancel it first, then seed the
        // authoritative status (a self-complete { subscribed, subscriptionId }).
        const statusKey = subscriptionsQueries.status.queryKey(sub.subscribableType, sub.subscribableId)
        await queryClient.cancelQueries({ queryKey: statusKey })
        queryClient.setQueryData<SubscriptionStatusResponse>(statusKey, { subscribed: true, subscriptionId: sub.id })
      } else if (sub.subscribableType === 'Card' || sub.subscribableType === 'SealedProduct') {
        // SetPriceAlertModal reads the per-item price-alert list cache. Append
        // optimistically (dedupe by id for safety) but only when the cache
        // already exists — don't seed a partial one for an item whose modal was
        // never opened. We deliberately do NOT invalidate here: create doesn't
        // trigger a refetch elsewhere, and firing one would let its in-flight
        // response (a pre-create snapshot) clobber a rapid second alert.
        const forItemKey = subscriptionsQueries.forItem.queryKey(sub.subscribableType, sub.subscribableId)
        queryClient.setQueryData<SubscriptionsResponse>(forItemKey, (old) =>
          old ? { subscriptions: [...old.subscriptions.filter((s) => s.id !== sub.id), sub] } : old,
        )
      }
    },
  })
  return withIdempotency(mutation)
}

export function useUpdateSubscription() {
  const queryClient = useQueryClient()
  type Vars = { id: number; data: UpdateSubscriptionData; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, data, idempotencyKey }: Vars) => updateSubscription(id, data, idempotencyKey),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: subscriptionsQueries.list.queryKey() })
      const previous = queryClient.getQueryData<SubscriptionsResponse>(subscriptionsQueries.list.queryKey())
      queryClient.setQueryData<SubscriptionsResponse>(subscriptionsQueries.list.queryKey(), (old) => {
        if (!old) return old
        return {
          subscriptions: old.subscriptions.map((s) => (s.id === id ? { ...s, ...data } : s)),
        }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(subscriptionsQueries.list.queryKey(), context.previous)
      }
    },
    onSuccess: (resp, { id }) => {
      queryClient.setQueryData<SubscriptionsResponse>(subscriptionsQueries.list.queryKey(), (old) => {
        if (!old) return old
        return {
          subscriptions: old.subscriptions.map((s) => (s.id === id ? resp.subscription : s)),
        }
      })
    },
  })
  return withIdempotency(mutation)
}

export function useDeleteSubscription() {
  const queryClient = useQueryClient()
  type Vars = { id: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, idempotencyKey }: Vars) => deleteSubscription(id, idempotencyKey),
    ...onSuccessInvalidate(queryClient, 'subscriptions'),
  })
  type PublicVars = number | Vars
  const normalize = (vars: PublicVars): Vars => (typeof vars === 'number' ? { id: vars } : vars)
  return withIdempotency(mutation, normalize)
}

export function useBulkCreateSubscriptions() {
  const queryClient = useQueryClient()
  type Vars = {
    kind: SubscriptionKind
    thresholdValue: number
    idempotencyKey?: string
  }
  const mutation = useMutation({
    mutationFn: ({ kind, thresholdValue, idempotencyKey }: Vars) =>
      bulkCreateFromBuylist(kind, thresholdValue, idempotencyKey),
    ...onSuccessInvalidate(queryClient, 'subscriptions'),
  })
  return withIdempotency(mutation)
}
